import { Layout } from '@/components/Layout';
import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import DeactivateModal from './DeactivateModal';
import './account-screen.styles.css';
import { axiosInstance } from '@/services/axios.service';
import { AuthenticationState, BaseResponse, SidebarState } from '@/types';
import { ENDPOINTS } from '@/constants/endpoints';
import { GetX4bUserDto, UpdateX4bUserDto } from '../UpdateX4bUserDto';
import { useQuery } from '@tanstack/react-query';
import { RemoveUserRequest } from '../RemoveUserRequest';
import { useNavigate } from 'react-router-dom';
import routes from '@/constants/routes';
import { useAuthenticationStore, useSidebarStore } from '@/store';
import { object, string } from 'yup';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputController } from '@/components/inputs/InputController';
import TextInput from '@/components/inputs/TextInput';

type TFormFields = Required<Pick<GetX4bUserDto, 'FirstName' | 'Email' | 'Phone'>>;

export const schema = object({
	FirstName: string().trim().required(VALIDATION_MESSAGES.required)
		.test('no-numbers', 'Names cannot contain numbers', function (value) {
			if (!value) return true; // Let required validation handle empty values
			return !/\d/.test(value);
		}),
	Email: string()
		.trim()
		.required(VALIDATION_MESSAGES.required)
		.email('Invalid email format'),
	Phone: string()
		.trim()
		.required(VALIDATION_MESSAGES.required)
});

const Account = () => {
	const form = useForm<TFormFields>({
		resolver: yupResolver(schema),
		mode: 'all'
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	//const [showBanner, setShowBanner] = useState(false);
	const navigate = useNavigate();
	const authenticationStore = useAuthenticationStore(
		(state: AuthenticationState) => state,
	);
	const sidebarStore = useSidebarStore((state: SidebarState) => state);

	const userQ = useQuery({
		queryKey: ['Account.User'],
		queryFn: async () => {
			const result = await axiosInstance.post<GetX4bUserDto>(ENDPOINTS['/api/X4bUser/GetById'], {});
			return result.data;
		}
	});
	useEffect(() => {
		if (userQ.data) {
			form.reset(userQ.data);
		}
	}, [userQ.data, form]);

	return (
		<Layout>
			{userQ.isLoading ? (
				<div className="account-container">
					<div className="account-loading-spinner" />
				</div>
			) : (
				<div className="w-[516px]">
					<h1 className="account-title">Account</h1>

					<div className="account-section p-input-filled">
						<h2 className="account-section-title">
							Profile Display Name
						</h2>
						<p className="account-section-description">
							This will show up as the name of the profile in the
							Xpress app
						</p>
						<InputController id="FirstName" control={form.control}>
							<TextInput label="Name" placeholder="Name" />
						</InputController>
					</div>

					<div className="account-section">
						<h2 className="account-section-title">
							Contact Details
						</h2>
						<div className={`account-input-group p-input-filled ${!!form.formState.errors.Email ? 'has-error' : ''}`}>
							<InputController id="Email" control={form.control}>
								<TextInput label="Email Address" placeholder="Email Address" />
							</InputController>

							<InputController id="Phone" control={form.control}>
								<TextInput label="Phone Number" placeholder="Phone Number" keyfilter={/^[0-9+]*$/} />
							</InputController>
						</div>
					</div>

					<div className="account-spacing-small" />

					<RoundedFilledButton
						id="save-changes"
						label="Save Changes"
						disabled={!form.formState.isValid}
						onClick={
							form.handleSubmit(async (dto: TFormFields) => {
								const result = await axiosInstance.post<BaseResponse>(
									ENDPOINTS['/api/X4bUser/UpdateUser'], {
										UserId: userQ.data?.Id,
										...dto
									} as UpdateX4bUserDto);
								showNotification('Account updated successfully.');

							})}
						className="save-changes-button"
					/>

					<div className="account-spacing-large" />

					<div className="account-divider" />

					<div className="account-spacing-medium" />

					<div>
						<h2 className="account-section-title">
							Deactivate Account
						</h2>
						<p className="text-label-medium text-primary pb-[36px] font-poppins">
							Close down your Xpress for Business account
						</p>

						<RoundedFilledButton
							id="deactivate-account"
							label="Deactivate"
							onClick={() => setIsModalOpen(true)}
							className="deactivate-button"
						/>

						<DeactivateModal
							isOpen={isModalOpen}
							onClose={() => setIsModalOpen(false)}
							onDeactivate={async (rq: RemoveUserRequest) => {
								await axiosInstance.post<BaseResponse>(ENDPOINTS['/api/X4bUser/RemoveUser'], { ...rq });
								showNotification('Account deactivated successfully.');
								setIsModalOpen(false);
								authenticationStore.resetState();
								sidebarStore.resetState();

								navigate(routes['/login']);
							}}
						/>
					</div>
				</div>
			)}
		</Layout>
	);
};

export default Account;
